import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex justify-between items-center text-left"
        id="FAQ_button"
      >
        <span className="font-medium text-gray-900">{question}</span>
        <ChevronDownIcon
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pb-4 text-gray-600 space-y-2">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Help = () => {
  const faqs = [
    {
      question: "How do I upload my outfits?",
      answer: (
        <>
          <p>To upload your outfits:</p>
          <ol className="list-decimal ml-4 mt-2">
            <li>Go to the Upload page</li>
            <li>View the required examples to understand best practices</li>
            <li>Drag and drop your images or click to browse</li>
            <li>You can upload up to 10 images at once</li>
            <li>Make sure your images are clear and well-lit</li>
          </ol>
        </>
      )
    },
    {
      question: "What's the best way to photograph my clothes?",
      answer: (
        <>
          <p>For the best results:</p>
          <ul className="list-disc ml-4 mt-2">
            <li>Use good lighting (natural light works best)</li>
            <li>Photograph against a plain background</li>
            <li>Lay items flat or use a hanger</li>
            <li>Capture the entire item in frame</li>
            <li>Avoid shadows or wrinkles when possible</li>
          </ul>
        </>
      )
    },
    {
      question: "How does outfit generation work?",
      answer: (
        <>
          <p>Our AI-powered outfit generation works in several steps:</p>
          <ol className="list-decimal ml-4 mt-2">
            <li>Upload your clothing items to your virtual closet (at least 10 items for each top, bottom, and shoe)</li>
            <li>Go to the Generate page</li>
            <li>Describe the occasion or style you're looking for</li>
            <li>Our AI will analyze your closet and suggest complete outfits</li>
            <li>You can save your favorite combinations for later</li>
          </ol>
        </>
      )
    },
    {
      question: "What file types are supported?",
      answer: (
        <>
          <p>We support the following image formats:</p>
          <ul className="list-disc ml-4 mt-2">
            <li>JPEG/JPG</li>
            <li>PNG</li>
            <li>WebP</li>
          </ul>
          <p className="mt-2">Maximum file size: 2MB per image</p>
        </>
      )
    },
    {
      question: "How do I manage my subscription?",
      answer: (
        <>
          <p>To manage your subscription:</p>
          <ol className="list-decimal ml-4 mt-2">
            <li>Click on your profile picture or menu icon in the top right</li>
            <li>Select "Manage Subscription" from the dropdown menu</li>
            <li>You'll be taken to your subscription management page</li>
            <li>Here you can upgrade, downgrade, or cancel your plan</li>
          </ol>
        </>
      )
    }
  ];

  const troubleshootingSteps = [
    "Clear your browser cache and cookies",
    "Try using a different browser",
    "Check your internet connection",
    "Make sure your images meet the size and format requirements",
    "If using mobile, try on desktop for better upload experience"
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Help Center</h1>
        <p className="text-gray-600">Find answers to common questions and get support</p>
      </div>

      {/* Quick Support Card */}
      <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg p-6 mb-12 text-white">
        <h2 className="text-xl font-semibold mb-4">Need immediate help?</h2>
        <p className="mb-4">Contact our support team directly:</p>
        <a 
          href="mailto:support@stylegenai.com"
          className="inline-flex items-center bg-white text-purple-600 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
          id="support_email"
        >
          support@stylegenai.com
        </a>
      </div>

      {/* FAQs Section */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
        <div className="bg-white rounded-lg shadow">
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </section>

      {/* Troubleshooting Section */}
      {/* <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-6">Troubleshooting Steps</h2>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600 mb-4">If you're experiencing issues, try these steps:</p>
          <ul className="space-y-3">
            {troubleshootingSteps.map((step, index) => (
              <li key={index} className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mr-3">
                  {index + 1}
                </span>
                <span className="text-gray-700">{step}</span>
              </li>
            ))}
          </ul>
        </div>
      </section> */}

      {/* Contact Support Section */}
      <section>
        <h2 className="text-2xl font-semibold mb-6">Still Need Help?</h2>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600 mb-4">
            If you couldn't find the answer you're looking for, our support team is here to help.
            Please email us with details about your issue, and we'll get back to you as soon as possible.
          </p>
          <div className="flex items-center space-x-4">
            <a
              href="mailto:support@stylegenai.com"
              className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-3 rounded-full hover:from-purple-600 hover:to-blue-600 transition-colors duration-200"
              id="contact_support_link"
            >
              Contact Support
            </a>
            <p className="text-sm text-gray-500">
              Response time: Usually within 24 hours
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Help; 